import * as z from 'zod';

export const RegisterCommentsWidgetRequest = z.object({
  categoryId: z
    .string()
    .describe('Widget category ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    resourceId: z.string().describe('Widget resource ID').min(1).max(128),
    componentId: z
      .string()
      .describe('Deprecated field')
      .min(1)
      .max(128)
      .optional(),
    dynamicPageTitle: z
      .string()
      .describe('Dynamic page tittle, if the widget is inside a dynamic page')
      .max(300)
      .optional()
      .nullable(),
    pageId: z
      .string()
      .describe('PageId of the page, which contains the widget')
      .min(1)
      .max(300)
      .optional()
      .nullable(),
    urlPath: z
      .string()
      .describe('Page Url, provided by the client')
      .max(500)
      .optional()
      .nullable(),
  }),
});
export const RegisterCommentsWidgetResponse = z.object({
  commentsWidget: z
    .object({
      _id: z
        .string()
        .describe('CommentsWidget ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      categoryId: z
        .string()
        .describe('Widget category ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      resourceId: z
        .string()
        .describe('Widget resource ID')
        .min(1)
        .max(128)
        .optional(),
      urlPath: z.string().describe('Path to widget location').optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Represents the time this CommentsResource was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this CommentsResource was last updated')
        .optional()
        .nullable(),
      componentId: z
        .string()
        .describe(
          'Component ID of the widget to which the category is attached to'
        )
        .max(300)
        .optional()
        .nullable(),
      commentCount: z
        .number()
        .int()
        .describe(
          'Count indicating how many published comments exist in category and resource'
        )
        .optional()
        .nullable(),
    })
    .describe('The registered CommentsWidget')
    .optional(),
});
export const QueryCommentsWidgetResourcesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Cursor query'),
});
export const QueryCommentsWidgetResourcesResponse = z.object({
  resourceIds: z.array(z.string()).max(100).optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const CreateCommentsWidgetRequest = z.object({
  commentsWidget: z
    .object({
      _id: z
        .string()
        .describe('CommentsWidget ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      categoryId: z
        .string()
        .describe('Widget category ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      resourceId: z.string().describe('Widget resource ID').min(1).max(128),
      urlPath: z.string().describe('Path to widget location').optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Represents the time this CommentsResource was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this CommentsResource was last updated')
        .optional()
        .nullable(),
      componentId: z
        .string()
        .describe(
          'Component ID of the widget to which the category is attached to'
        )
        .max(300)
        .optional()
        .nullable(),
      commentCount: z
        .number()
        .int()
        .describe(
          'Count indicating how many published comments exist in category and resource'
        )
        .optional()
        .nullable(),
    })
    .describe('CommentsWidget to be created'),
});
export const CreateCommentsWidgetResponse = z.object({
  _id: z
    .string()
    .describe('CommentsWidget ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  categoryId: z
    .string()
    .describe('Widget category ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  resourceId: z
    .string()
    .describe('Widget resource ID')
    .min(1)
    .max(128)
    .optional(),
  urlPath: z.string().describe('Path to widget location').optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Represents the time this CommentsResource was created')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Represents the time this CommentsResource was last updated')
    .optional()
    .nullable(),
  componentId: z
    .string()
    .describe('Component ID of the widget to which the category is attached to')
    .max(300)
    .optional()
    .nullable(),
  commentCount: z
    .number()
    .int()
    .describe(
      'Count indicating how many published comments exist in category and resource'
    )
    .optional()
    .nullable(),
});
export const GetCommentsWidgetRequest = z.object({
  commentsWidgetId: z
    .string()
    .describe('Id of the CommentsWidget to retrieve')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetCommentsWidgetResponse = z.object({
  _id: z
    .string()
    .describe('CommentsWidget ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  categoryId: z
    .string()
    .describe('Widget category ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  resourceId: z
    .string()
    .describe('Widget resource ID')
    .min(1)
    .max(128)
    .optional(),
  urlPath: z.string().describe('Path to widget location').optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Represents the time this CommentsResource was created')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Represents the time this CommentsResource was last updated')
    .optional()
    .nullable(),
  componentId: z
    .string()
    .describe('Component ID of the widget to which the category is attached to')
    .max(300)
    .optional()
    .nullable(),
  commentCount: z
    .number()
    .int()
    .describe(
      'Count indicating how many published comments exist in category and resource'
    )
    .optional()
    .nullable(),
});
export const UpdateCommentsWidgetRequest = z.object({
  _id: z
    .string()
    .describe('CommentsWidget ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  commentsWidget: z
    .object({
      _id: z
        .string()
        .describe('CommentsWidget ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      categoryId: z
        .string()
        .describe('Widget category ID')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      resourceId: z
        .string()
        .describe('Widget resource ID')
        .min(1)
        .max(128)
        .optional(),
      urlPath: z.string().describe('Path to widget location').optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
        ),
      _createdDate: z
        .date()
        .describe('Represents the time this CommentsResource was created')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Represents the time this CommentsResource was last updated')
        .optional()
        .nullable(),
      componentId: z
        .string()
        .describe(
          'Component ID of the widget to which the category is attached to'
        )
        .max(300)
        .optional()
        .nullable(),
      commentCount: z
        .number()
        .int()
        .describe(
          'Count indicating how many published comments exist in category and resource'
        )
        .optional()
        .nullable(),
    })
    .describe('CommentsWidget to be updated, may be partial'),
});
export const UpdateCommentsWidgetResponse = z.object({
  _id: z
    .string()
    .describe('CommentsWidget ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  categoryId: z
    .string()
    .describe('Widget category ID')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  resourceId: z
    .string()
    .describe('Widget resource ID')
    .min(1)
    .max(128)
    .optional(),
  urlPath: z.string().describe('Path to widget location').optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Represents the time this CommentsResource was created')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Represents the time this CommentsResource was last updated')
    .optional()
    .nullable(),
  componentId: z
    .string()
    .describe('Component ID of the widget to which the category is attached to')
    .max(300)
    .optional()
    .nullable(),
  commentCount: z
    .number()
    .int()
    .describe(
      'Count indicating how many published comments exist in category and resource'
    )
    .optional()
    .nullable(),
});
export const DeleteCommentsWidgetRequest = z.object({
  commentsWidgetId: z
    .string()
    .describe('Id of the CommentsWidget to delete')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('The revision of the CommentsWidget')
        .optional(),
    })
    .optional(),
});
export const DeleteCommentsWidgetResponse = z.object({});
export const QueryCommentsWidgetRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          categoryId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          resourceId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          urlPath: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          componentId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          commentCount: z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'categoryId',
                'resourceId',
                'urlPath',
                '_createdDate',
                '_updatedDate',
                'componentId',
                'commentCount',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('WQL expression'),
});
export const QueryCommentsWidgetResponse = z.object({
  commentsWidgets: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('CommentsWidget ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        categoryId: z
          .string()
          .describe('Widget category ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        resourceId: z
          .string()
          .describe('Widget resource ID')
          .min(1)
          .max(128)
          .optional(),
        urlPath: z.string().describe('Path to widget location').optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Represents the time this CommentsResource was created')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Represents the time this CommentsResource was last updated'
          )
          .optional()
          .nullable(),
        componentId: z
          .string()
          .describe(
            'Component ID of the widget to which the category is attached to'
          )
          .max(300)
          .optional()
          .nullable(),
        commentCount: z
          .number()
          .int()
          .describe(
            'Count indicating how many published comments exist in category and resource'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata')
    .optional(),
});
